import * as React from 'react'
import { useTheme } from '@mui/material/styles'
import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableContainer from '@mui/material/TableContainer'
import TableRow from '@mui/material/TableRow'
import { Stack, Typography } from '@mui/material'
import { stableSort, getComparator, Order, HeadCell } from '../../utils/tableFunctions'
import TextSearchField from '../common/TextSearchField'
import DateRangePicker from '../common/DateRangePicker'
import { EventOfProductDto } from '../../services/api/sharedTypes/Event'
import EnhancedTableHead from '../table/EnhancedTableHead'
import CustomTableCell from '../common/CustomTableCell'
import { convertToSearchString, getFormattedTimeSpanToReadableText, getNumberRoundedInFormattedString } from '../../utils/global-utils'
import { RouteConstants } from '../../navigation/navigation-types'
import { OrderState } from '../../services/api/sharedTypes/Common'
import OrderStateFilter from '../common/OrderStateFilter'
import CancelFiltersButton from '../common/CancelFiltersButton'
import TableButtonOpen from '../table/TableButtonOpen'

const headCells: readonly HeadCell[] = [
    {
        id: 'eventName',
        numeric: false,
        disablePadding: false,
        label: 'Název',
    },
    {
        id: 'startsAt',
        numeric: false,
        disablePadding: false,
        label: 'Datum od',
    },
    {
        id: 'endsAt',
        numeric: false,
        disablePadding: false,
        label: 'Datum do',
    },
    {
        id: 'operatingTime',
        numeric: false,
        disablePadding: false,
        label: 'Doba provozu',
    },
    {
        id: 'costs',
        numeric: true,
        disablePadding: false,
        label: 'Náklady',
        align: 'right',
    },
    {
        id: 'revenue',
        numeric: true,
        disablePadding: false,
        label: 'Výnos',
        align: 'right',
    },
    {
        id: 'profit',
        numeric: true,
        disablePadding: false,
        label: 'Zisk',
        align: 'right',
    },
    {
        id: 'buttons',
        numeric: false,
        disablePadding: false,
        label: '',
    },
]
interface EventTableProps {
    events: EventOfProductDto[]
    operatingTime: string
    costs: number
    revenue: number
    profit: number
}

const ProductEventTable: React.FC<EventTableProps> = ({ events, operatingTime, costs, revenue, profit }: EventTableProps) => {
    const [order, setOrder] = React.useState<Order>('desc')
    const [orderBy, setOrderBy] = React.useState<string>('startsAt')
    const [dateFrom, setDateFrom] = React.useState<Date | null>(null)
    const [dateTo, setDateTo] = React.useState<Date | null>(null)
    const [filteredEvents, setFilteredEvents] = React.useState<EventOfProductDto[]>(events)
    const [searchedValue, setSearchedValue] = React.useState<string>('')
    const [selectedOrderState, setSelectedOrderState] = React.useState<OrderState[]>([])

    const theme = useTheme()

    // Filter events
    React.useEffect(() => {
        const filtered = events
            .filter((row) => {
                const date = new Date(row.startsAt)
                if (dateFrom && date < dateFrom) {
                    return false
                }
                if (dateTo && date > dateTo) {
                    return false
                }
                if (searchedValue && !convertToSearchString(row.eventName).includes(convertToSearchString(searchedValue))) {
                    return false
                }
                return true
            })
            .filter((row) => {
                return selectedOrderState.length === 0 || selectedOrderState.includes(row.orderState)
            })
        setFilteredEvents(filtered)
    }, [events, dateFrom, dateTo, searchedValue, selectedOrderState])

    const handleRequestSort = (event: React.MouseEvent<unknown>, property: string) => {
        const isAsc = orderBy === property && order === 'asc'
        setOrder(isAsc ? 'desc' : 'asc')
        setOrderBy(property)
    }

    return (
        <>
            <Typography variant='h6' color='primary' mt={3} mb={1}>
                Události
            </Typography>

            <Stack pt={1} pb={3} direction='row' spacing={3}>
                <DateRangePicker
                    labelFrom='Datum od'
                    labelTo='Datum do'
                    onSetDateFrom={setDateFrom}
                    onSetDateTo={setDateTo}
                    dateFrom={dateFrom}
                    dateTo={dateTo}
                />
                <TextSearchField value={searchedValue} onChange={(e) => setSearchedValue(e.target.value)} />
                <OrderStateFilter orderState={selectedOrderState} onChange={setSelectedOrderState} />
                {
                    searchedValue || dateFrom || dateTo || selectedOrderState.length > 0
                        ? (
                            <CancelFiltersButton onClick={
                                () => {
                                    setSearchedValue('')
                                    setDateFrom(null)
                                    setDateTo(null)
                                    setSelectedOrderState([])
                                }
                            }
                            />
                        ) : null
                }
            </Stack>
            <Typography variant='body2' style={{ marginBottom: theme.spacing(3) }}>
                {filteredEvents.length} záznamů
            </Typography>
            <Box style={{ width: '100%' }}>
                <TableContainer>
                    <Table>
                        <EnhancedTableHead
                            order={order}
                            orderBy={orderBy}
                            onRequestSort={handleRequestSort}
                            rowCount={filteredEvents.length}
                            headCells={headCells}
                        />
                        <TableBody>
                            {stableSort(
                                filteredEvents.map((row) => {
                                    return {
                                        ...row,
                                        startsAt: new Date(row.startsAt).getTime(),
                                        endsAt: new Date(row.endsAt).getTime(),
                                        isConfirmed: row.isConfirmed ? 'Ano' : 'Ne',
                                    }
                                }),
                                getComparator(order, orderBy)
                            ).map((row, index: number) => {
                                return (
                                    <TableRow
                                        hover
                                        tabIndex={-1}
                                        key={index}
                                        style={{
                                            height: '42px',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        <CustomTableCell route={RouteConstants.events + '/' + row.eventId} isText>
                                            {row.eventName}
                                        </CustomTableCell>
                                        <CustomTableCell route={RouteConstants.events + '/' + row.eventId} isText>
                                            {new Date(row.startsAt).toLocaleDateString('cs')}
                                        </CustomTableCell>
                                        <CustomTableCell route={RouteConstants.events + '/' + row.eventId} isText>
                                            {new Date(row.endsAt).toLocaleDateString('cs')}
                                        </CustomTableCell>
                                        <CustomTableCell route={RouteConstants.events + '/' + row.eventId} isText>
                                            {getFormattedTimeSpanToReadableText(row.operatingTime)}
                                        </CustomTableCell>
                                        <CustomTableCell route={RouteConstants.events + '/' + row.eventId} align='right'>
                                            {getNumberRoundedInFormattedString(row.costs)}
                                        </CustomTableCell>
                                        <CustomTableCell route={RouteConstants.events + '/' + row.eventId} align='right'>
                                            {getNumberRoundedInFormattedString(row.revenue)}
                                        </CustomTableCell>
                                        <CustomTableCell route={RouteConstants.events + '/' + row.eventId} align='right'>
                                            {getNumberRoundedInFormattedString(row.profit)}
                                        </CustomTableCell>
                                        <CustomTableCell route={RouteConstants.events + '/' + row.eventId} align='right'>
                                            <TableButtonOpen route={RouteConstants.events + '/' + row.eventId} />
                                        </CustomTableCell>
                                    </TableRow>
                                )
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
                <Stack spacing={2} direction='row' py={3} ml={3} mb={5}>
                    <Typography variant='body2' fontWeight='bold'>
                        Doba provozu:
                    </Typography>
                    <Typography variant='body2'>{getFormattedTimeSpanToReadableText(operatingTime)}</Typography>

                    <Typography variant='body2' fontWeight='bold'>
                        Náklady:
                    </Typography>
                    <Typography variant='body2'>{getNumberRoundedInFormattedString(costs)}</Typography>

                    <Typography variant='body2' fontWeight='bold'>
                        Výnos:
                    </Typography>
                    <Typography variant='body2'>{getNumberRoundedInFormattedString(revenue)}</Typography>

                    <Typography variant='body2' fontWeight='bold'>
                        Zisk:
                    </Typography>
                    <Typography variant='body2'>{getNumberRoundedInFormattedString(profit)} </Typography>
                </Stack>
            </Box>
        </>
    )
}

export default ProductEventTable
